/**
 * RBAC для бек-офісу. Роль зберігається у `admins/{uid}.role`.
 *
 * Backward compat: документ без поля `role` трактується як `"owner"` —
 * для існуючих адмінів, доданих до введення RBAC.
 */
export type Role = "owner" | "ops" | "accountant";

export const ROLES: Role[] = ["owner", "ops", "accountant"];

export const ROLE_LABELS: Record<Role, string> = {
  owner: "Власник",
  ops: "Ops",
  accountant: "Бухгалтер",
};

export type RoutePath =
  | "/leads"
  | "/pl-discovery"
  | "/analytics"
  | "/risks"
  | "/settings"
  | "/tracker"
  | "/billing"
  | "/strategy"
  | "/admins"
  | "/admins/audit";

/** Секція = група маршрутів з однаковим доступом. */
export type SectionKey =
  | "landings"
  | "customers"
  | "delivery"
  | "finance"
  | "strategy"
  | "team";

export function canAccessLandings(role: Role | null): boolean {
  return role === "owner" || role === "ops";
}

export function canAccessCustomers(role: Role | null): boolean {
  return role === "owner" || role === "ops";
}

export function canAccessFinance(role: Role | null): boolean {
  return role === "owner" || role === "accountant";
}

export function canAccessDelivery(_role: Role | null): boolean {
  // Трекер відкритий усім автентифікованим користувачам бекофісу, незалежно
  // від ролі (навіть без ролі). Навмисно ігноруємо role.
  return true;
}

export function canAccessStrategy(role: Role | null): boolean {
  return role === "owner";
}

export function canAccessAdmins(role: Role | null): boolean {
  return role === "owner";
}

export function canAccessSection(role: Role | null, section: SectionKey): boolean {
  switch (section) {
    case "landings":
      return canAccessLandings(role);
    case "customers":
      return canAccessCustomers(role);
    case "delivery":
      return canAccessDelivery(role);
    case "finance":
      return canAccessFinance(role);
    case "strategy":
      return canAccessStrategy(role);
    case "team":
      return canAccessAdmins(role);
  }
}

export function canAccessRoute(role: Role | null, path: RoutePath): boolean {
  if (path === "/leads" || path === "/pl-discovery" || path === "/analytics") return canAccessLandings(role);
  if (path === "/risks" || path === "/settings") return canAccessCustomers(role);
  if (path === "/tracker") return canAccessDelivery(role);
  if (path === "/billing") return canAccessFinance(role);
  if (path === "/strategy") return canAccessStrategy(role);
  if (path === "/admins" || path === "/admins/audit") return canAccessAdmins(role);
  return false;
}

/**
 * Куди вести користувача, коли він заходить на "/" або коли його
 * редіректить з недоступного маршруту.
 */
export function firstAccessibleRoute(role: Role | null): RoutePath | null {
  if (canAccessLandings(role)) return "/leads";
  if (canAccessCustomers(role)) return "/risks";
  if (canAccessDelivery(role)) return "/tracker";
  if (canAccessFinance(role)) return "/billing";
  if (canAccessStrategy(role)) return "/strategy";
  if (canAccessAdmins(role)) return "/admins";
  return null;
}
